import { Archive } from 'lucide-react';
import { useState } from 'react';
import { tokens } from '../lib/api';
import { useParent } from './ParentContext';

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

/**
 * Download my records (#883): every record as one ZIP — each child's portfolio and transcript (PDF), report card (Word),
 * the planner, grades, attendance, journal, reading log and activity as CSV for Excel or Numbers, and work samples.
 * After cancelling, it says how long the records are kept.
 */
export function DownloadRecords({ heading = true }: { heading?: boolean }) {
  const { parent, toast } = useParent();
  const [busy, setBusy] = useState(false);
  const records = parent.billing?.records;
  async function download() {
    setBusy(true);
    try {
      const res = await fetch('/api/me/records.zip', { headers: { Authorization: `Bearer ${tokens.parent() ?? ''}` } });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'Couldn’t download your records.');
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'classguardian-records.zip';
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  if (records?.deletedAt)
    return <p className="records-kept">This family’s records were deleted on {day(records.deletedAt)}. Subscribe to start fresh.</p>;
  return (
    <div className="stack download-records">
      {heading && (
        <h3 className="with-icon">
          <Archive size={16} /> Download my records
        </h3>
      )}
      {!!records?.deleteAt && (
        <p className="records-kept">
          Your records are kept until <strong>{day(records.deleteAt)}</strong>. Download them before then, or subscribe again to keep everything.
        </p>
      )}
      <p className="muted small">
        One file with each child’s portfolio and transcript (PDF), report card (Word), the planner, grades, attendance, journal, reading log and activity as
        spreadsheets for Excel or Numbers, and their work samples.
      </p>
      <button type="button" className="btn primary" onClick={download} disabled={busy}>
        <Archive size={16} /> {busy ? 'Getting everything ready…' : 'Download my records (.zip)'}
      </button>
    </div>
  );
}
